<template>
  <div class="o-card container-fluid">
    <h4>{{ $t("navigation.tksCalculating") }}</h4>
    <hr />
    <no-auth v-if="!hasAuthorize.add"></no-auth>
    <div class="row" v-else>
      <div class="es-xs-12 es-md-6 es-lg-4">
        <TksGetGrainNames
          v-model="form.grainName"
          :label="$t('tks.grainNames')"
        />
        <div v-if="form.grainName">
          <BaseInput
            v-model="form.grainName.specificHeat"
            block
            size="sm"
            :label="$t('tks.specificHeat')"
            readonly
          />
          <BaseInput
            v-model="form.grainName.permeability"
            size="sm"
            block
            :label="$t('tks.permeability')"
            readonly
          />
        </div>
        <div
          class="u-display-flex u-align-items-center u-justify-content-center u-margin-top"
        >
          <img src="@/assets/images/tksImage.png" alt="tks" />
        </div>
      </div>
      <div class="es-xs-12 es-md-6 es-lg-4">
        <TksGetDeviceNames
          :disabled="!form.grainName"
          v-model="form.deviceName"
          :label="$t('tks.deviceNames')"
          @option:selected="getCapacityByDevice"
        />
        <div v-if="form.deviceName">
          <BaseInput
            v-model="form.capacity"
            block
            size="sm"
            :label="$t('tks.capacity')"
            suffix="kW"
            readonly
          />
          <BaseInput
            v-model="result.fanFlowRate"
            size="sm"
            block
            suffix="m³/h"
            :label="$t('tks.fanFlow')"
            readonly
          />
        </div>
        <div>
          <h5 class="u-margin-top u-margin-bottom-small">
            {{ $t("tks.grainInitialFinalTemperatures") }}
          </h5>
          <BaseInput
            v-model="form.airInitialTemperature"
            block
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
            size="sm"
            suffix="°C"
            :label="$t('tks.airInitialTemperature')"
          />
          <BaseInput
            v-model="form.airInitialHumidity"
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
            block
            size="sm"
            suffix="%"
            :label="$t('tks.airInitialHumidity')"
          />
          <BaseInput
            v-model="form.airFinalTemperature"
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
            block
            size="sm"
            suffix="°C"
            :label="$t('tks.airFinalTemperature')"
          />
          <BaseInput
            v-model="form.airFinalHumidity"
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
            block
            size="sm"
            suffix="%"
            :label="$t('tks.airFinalHumidity')"
          />
          <BaseInput
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
            v-model="form.siloHeight"
            block
            suffix="m"
            size="sm"
            :label="$t('tks.siloHeight')"
          />
          <BaseInput
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
            :value="200"
            block
            readonly
            size="sm"
            suffix="Pa"
            :label="$t('tks.channel')"
          />
          <BaseInput
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
            :value="800"
            block
            readonly
            suffix="Pa"
            size="sm"
            :label="$t('tks.intoDevice')"
          />
          <BaseInput
            class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
            v-model="form.grainWeight"
            block
            suffix="TON"
            size="sm"
            :label="$t('tks.grainWeight')"
          />
          <Button
            @click="getCalculate"
            :disabled="!form.capacity"
            block
            variant="warning"
            class="u-margin-top-small"
          >
            {{ $t("common.calculate") }}
          </Button>
        </div>
      </div>
      <div class="es-xs-12 es-md-6 es-lg-4">
        <div class="row u-align-items-center">
          <div class="es-xs-9 u-margin-0 u-padding-0">
            <BaseInput
              class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
              v-model="result.hour"
              block
              readonly
              :suffix="$t('tks.hour')"
              size="sm"
              :label="$t('tks.coolingDuration')"
            />
          </div>
          <div class="es-xs-3 u-margin-0 u-padding-0">
            <BaseInput
              v-model="result.day"
              block
              readonly
              :suffix="$t('tks.day')"
              size="sm"
            />
          </div>
        </div>
        <BaseInput
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          v-model="result.connectionDimensions"
          block
          readonly
          size="sm"
          suffix="mm"
          :label="$t('tks.connectionDimensions')"
        />
        <BaseInput
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          v-model="result.maximumFanAirFlowRate"
          block
          readonly
          suffix="m³/h"
          size="sm"
          :label="$t('tks.maximumFanAirFlowRate')"
        />
        <BaseInput
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          :value="6000"
          block
          readonly
          suffix="Pa"
          size="sm"
          :label="$t('tks.maximumFanPressure')"
        />
        <BaseInput
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          v-model="result.electricalData"
          block
          readonly
          size="sm"
          :label="$t('tks.electricalData')"
        />
        <BaseInput
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          v-model="result.operatingCurrent"
          block
          readonly
          suffix="A"
          size="sm"
          :label="$t('tks.operatingCurrent')"
        />
        <BaseInput
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          v-model="result.maximumOperatingCurrent"
          block
          readonly
          suffix="A"
          size="sm"
          :label="$t('tks.maximumOperatingCurrent')"
        />
        <BaseInput
          class="u-display-flex u-align-items-center u-justify-content-space-between u-margin-xxsmall u-flex-1-with-child"
          v-model="result.operatingPowerConsumption"
          block
          readonly
          size="sm"
          suffix="kW"
          :label="$t('tks.operatingPowerConsumption')"
        />
        <div v-if="result.silo">
          <h6 class="u-margin-top-small">{{ $t("tks.pressureLoss") }}</h6>
          <ul class="c-list">
            <li class="c-list__item">
              {{ $t("tks.silo") }}
              <span> {{ result.silo }} Pa </span>
            </li>
            <li class="c-list__item">
              {{ $t("tks.channel") }}
              <span> 200 Pa </span>
            </li>
            <li class="c-list__item">
              {{ $t("tks.intoDevice") }}
              <span> 800 Pa </span>
            </li>
            <li class="c-list__item">
              {{ $t("tks.total") }}
              <span> {{ result.total }} Pa </span>
            </li>
          </ul>
        </div>
        <Button
          @click="save"
          block
          :disabled="!result.capacity"
          variant="primary"
          class="u-margin-top-small"
        >
          <i class="icofont-save"></i> {{ $t("common.save") }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import TksGetGrainNames from "@/components/Searches/TksGetGrainNames.vue";
import TksGetDeviceNames from "@/components/Searches/TksGetDeviceNames.vue";
import BaseInput from "@/components/Base/Inputs/BaseInput.vue";
import Requests from "@/services/Requests";
import Button from "@/components/Base/Button.vue";
import authorize from "./authorize";
export default {
  components: {
    TksGetGrainNames,
    TksGetDeviceNames,
    BaseInput,
    Button,
  },
  mixins: [authorize],
  data() {
    return {
      form: {
        grainName: null,
        deviceName: null,
        airInitialTemperature: 35,
        airInitialHumidity: 65,
        airFinalTemperature: 23,
        airFinalHumidity: 45,
        siloHeight: 34,
        grainWeight: 700,
        capacity: 0,
      },
      result: {
        silo: null,
        total: null,
        hour: null,
        day: null,
        selectedCerealType: null,
        airInitialTemperature: null,
        airInitialHumidity: null,
        airFinalTemperature: null,
        airFinalHumidity: null,
        siloHeight: null,
        grainWeight: null,
        coolingDurationHouse: null,
        coolingDurationDay: null,
        connectionDimensions: null,
        maximumFanAirFlowRate: null,
        maximumFanPressure: null,
        electricalData: null,
        operatingCurrent: null,
        maximumOperatingCurrent: null,
        operatingPowerConsumption: null,
        fanFlowRate: null,
        capacity: null,
      },
    };
  },
  watch: {
    "form.grainName": {
      handler() {
        this.form.deviceName = null;
      },
    },
  },
  methods: {
    validation() {
      if (!this.form.grainName)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("tks.grainNames"),
          })
        );
      else if (!this.form.deviceName)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("tks.deviceNames"),
          })
        );
      else if (!this.form.airInitialTemperature)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("tks.airInitialTemperature"),
          })
        );
      else if (!this.form.airInitialHumidity)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("tks.airInitialHumidity"),
          })
        );
      else if (!this.form.airFinalTemperature)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("tks.airFinalTemperature"),
          })
        );
      else if (!this.form.airFinalHumidity)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("tks.airFinalHumidity"),
          })
        );
      else if (!this.form.siloHeight)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("tks.siloHeight"),
          })
        );
      else if (!this.form.grainWeight)
        return this.VALIDATION_ERROR_MESSAGE(
          this.$t("message.required.description", {
            field: this.$t("tks.grainWeight"),
          })
        );
      return "OK";
    },
    VALIDATION_ERROR_MESSAGE(message) {
      this.$store.dispatch("SET_ACTION_MODAL", {
        isLoading: false,
        status: true,
        message: message,
        variant: "danger",
        duration: 2500,
      });
      return "FAIL";
    },
    async getCapacityByDevice(option) {
      const validation = this.validation();
      if (validation === "FAIL") return;
      console.log(option);

      const response = await Requests.Tks.GetCapacityByDevice({
        grainId: this.form.grainName?.id,
        deviceId: option?.key || this.form.deviceName?.key,
        airInitialTemperature: this.form.airInitialTemperature,
        airInitialHumidity: this.form.airInitialHumidity,
        airFinalTemperature: this.form.airFinalTemperature,
        airFinalHumidity: this.form.airFinalHumidity,
        siloHeight: this.form.siloHeight,
        grainWeight: this.form.grainWeight,
      });
      if (response.message === "OK") {
        this.form.capacity = response.data;
      }
      console.log(response);
    },
    async getCalculate(e, loading) {
      const validation = this.validation();
      if (validation === "FAIL") return;
      loading(true);
      this.$store.dispatch("SET_ACTION_MODAL", {
        isLoading: true,
        status: true,
        duration: 20 * 60 * 1000,
      });
      const response = await Requests.Tks.Calculate({
        deviceId: this.form.deviceName?.key,
        grainId: this.form.grainName?.id,
        airInitialTemperature: this.form.airInitialTemperature,
        airInitialHumidity: this.form.airInitialHumidity,
        airFinalTemperature: this.form.airFinalTemperature,
        airFinalHumidity: this.form.airFinalHumidity,
        siloHeight: this.form.siloHeight,
        grainWeight: this.form.grainWeight,
      });
      if (response.message === "OK") {
        this.result = response.data;
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: false,
        });
      } else {
        this.VALIDATION_ERROR_MESSAGE(
          this.$t(`message.${response.message}.description`)
        );
      }
      loading(false);
    },
    async save(e, loading) {
      const validation = this.validation();
      if (validation === "FAIL") return;
      loading(true);
      this.$store.dispatch("SET_ACTION_MODAL", {
        isLoading: true,
        status: true,
        duration: 20 * 60 * 1000,
      });
      const response = await Requests.Tks.Create({
        deviceId: this.form.deviceName?.key,
        grainId: this.form.grainName?.id,
        airInitialTemperature: this.form.airInitialTemperature,
        airInitialHumidity: this.form.airInitialHumidity,
        airFinalTemperature: this.form.airFinalTemperature,
        airFinalHumidity: this.form.airFinalHumidity,
        siloHeight: this.form.siloHeight,
        grainWeight: this.form.grainWeight,
      });
      if (response.message === "OK") {
        this.$router.push({
          name: "tks-calculating-detail",
          params: { id: response.data.id },
        });
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: false,
        });
      } else {
        this.VALIDATION_ERROR_MESSAGE(
          this.$t(`message.${response.message}.description`)
        );
      }
      loading(false);
    },
  },
};
</script>

<style></style>
